import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Archive } from "lucide-react";
import type { CompanyPage } from '@/hooks/useManagementAPI';

interface ArchiveCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  companyName: string;
  pages: CompanyPage[];
  isLoading: boolean;
}

export function ArchiveCompanyModal({
  isOpen,
  onClose,
  onConfirm,
  companyName,
  pages,
  isLoading
}: ArchiveCompanyModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={() => !isLoading && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
              <Archive className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Archive Company
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-gray-600 mb-4">
            This will archive all <span className="font-semibold">{pages.length}</span> pricing page{pages.length !== 1 ? 's' : ''} for <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm">{companyName}</span>.
          </p>

          {pages.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Pages to be archived:</p>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-600 max-h-32 overflow-y-auto">
                {pages.map((page) => (
                  <li key={page.id}>
                    {page.subslug || '(main page)'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <p className="text-sm text-orange-800">
              The company will be hidden from lists. You can unarchive it later to restore visibility.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Archiving..." : "Archive Company"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
